import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';

@Entity()
export class Teacher extends MongoEntity {
  static override _class = 'Teacher';
  static override _collectionName = 'teachers';

  @Field(() => String)
  firstName!: string;

  @Field(() => String)
  lastName!: string;

  @Field(() => String)
  school!: string;

  /** Canonical {@link Subject} keys picked from the fixed list, plus any custom subject name the
   * teacher typed in themselves — not restricted to the enum, same as {@link Student.subjects}. */
  @Field(() => [String])
  subjects!: string[];

  /**
   * Classes the teacher has taught, accumulated automatically from the `class` set on their exams
   * (see syncTeacherClassFromExam) — never edited directly. Empty until the first exam gets a class.
   */
  @Field(() => [String], { default: v => v ?? [] })
  classes!: string[];

  @Field(() => String)
  email!: string;

  @Field(() => String)
  userID!: string;

  @Field(() => String)
  language?: string;

  /** Opt-in for notification mails (e.g. released exam results). Absent means off. */
  @Field(() => Boolean)
  emailNotifications?: boolean;

  @Field(() => String)
  gradingKindness?: string;

  @Field(() => Boolean)
  isAdmin?: boolean;

  @Field(() => Memory)
  memory?: Memory;

  @Field(() => GrammarPreferences)
  grammarPreferences?: GrammarPreferences;
}

/**
 * Default (per-teacher) choice of whether to always use the Fehlerindex instead of detected/merged
 * grammar rules. A per-exam override (Exam.grammarFiSettingsJson.alwaysUseFi) always wins over this
 * when set; see shared/grammar-rules-resolution.ts.
 */
@Entity()
export class GrammarPreferences extends AdornisEntity {
  static override _class = 'GrammarPreferences';

  @Field(() => Boolean)
  alwaysUseFi!: boolean;

  /**
   * Minimum cumulative changed-word count (across one or more edits to the same task's answer text,
   * see SubmissionTask.pendingLanguageUseWordDelta) before an answer edit auto-triggers a language-use
   * regrade of the LanguageUseScheme group(s) the task belongs to. Below this, an edit still resets
   * nothing — it accumulates until the total crosses the cutoff.
   */
  @Field(() => Int, { default: v => v ?? 5 })
  languageUseRegradeWordCutoff!: number;

  /**
   * Same idea as languageUseRegradeWordCutoff, for the CONTENT regrade an answer edit already
   * auto-triggers. Default 0 means "no gate" — every qualifying edit still regrades content
   * immediately, i.e. today's behavior, unchanged unless a teacher raises this.
   */
  @Field(() => Int, { default: v => v ?? 0 })
  contentRegradeWordCutoff!: number;
}

/**
 * A single stored preference rule. `subject` is the explicit string 'general' when the rule applies
 * across all subjects, or a canonical subject key (see {@link Subject}) when it applies to one subject only.
 */
@Entity()
export class MemoryRule extends AdornisEntity {
  static override _class = 'MemoryRule';

  /**
   * Embedded rules are stored without their own `_id`. The BaseQL client refuses to construct an
   * array of entities that lack a unique key (see entities/construct.js), so deserializing a
   * non-empty rule list would otherwise throw and abort the whole teacher query. Derive a stable
   * cache key from the rule's content — subject + text is unique enough within a teacher's memory.
   */
  static override resolveID(data: any) {
    return `${data?.subject ?? 'general'}::${data?.rule ?? ''}`;
  }

  @Field(() => String)
  rule!: string;

  @Field(() => String)
  subject!: string;
}

/**
 * Per-category lists of preference rules, oldest first (newest appended). Each category is capped
 * (see memory.tool.ts) and curated by a lightweight conflict-manager model on every save.
 */
@Entity()
export class Memory extends MongoEntity {
  static override _class = 'Memory';

  @Field(() => [MemoryRule])
  gradingStyle!: MemoryRule[];

  @Field(() => [MemoryRule])
  chattingStyle!: MemoryRule[];

  @Field(() => [MemoryRule])
  masterSolutionStyle!: MemoryRule[];

  @Field(() => [MemoryRule])
  gradingSchemeStyle!: MemoryRule[];
}
